import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { catalogApi, studentApi } from '@/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  PageHeader,
  Select,
  useToast,
} from '@/components/ui';
import { CertificationsCard } from '@/features/student/CertificationsCard';
import { errorMessage } from '@/hooks/useApiError';
import { skillSchema } from '@/validations';

const PROFICIENCY_LABELS = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
  EXPERT: 'Expert',
};

export function StudentSkillsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [query, setQuery] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-skills'],
    queryFn: studentApi.listSkills,
  });

  // Suggestions come from the curated catalogue, so free text stays normalised.
  const { data: suggestions } = useQuery({
    queryKey: ['skills', query],
    queryFn: () => catalogApi.listSkills(query || undefined),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(skillSchema),
    defaultValues: { proficiency: 'INTERMEDIATE' },
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['student-skills'] });
    void queryClient.invalidateQueries({ queryKey: ['student-profile'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
  }

  const remove = useMutation({
    mutationFn: (skillId) => studentApi.removeSkill(skillId),
    onSuccess: () => {
      toast.success('Skill removed');
      invalidate();
    },
    onError: (mutationError) => toast.error(errorMessage(mutationError)),
  });

  async function onSubmit(values) {
    try {
      const saved = await studentApi.addSkill({
        skill_name: values.skill_name,
        proficiency: values.proficiency,
      });
      // Adding a skill already on the profile only sets its level, so say what
      // actually happened. Matched by catalogue id: an alias ("reactjs") lands
      // on the same skill as its name.
      const existing = data?.find((entry) => entry.skill.id === saved?.skill?.id);
      const name = saved?.skill?.name ?? values.skill_name;
      if (!existing) {
        toast.success('Skill added');
      } else if (existing.proficiency === saved.proficiency) {
        toast.notify(`${name} is already on your profile`, 'info');
      } else {
        toast.success(`${name} updated to ${PROFICIENCY_LABELS[saved.proficiency]}`);
      }
      reset({ skill_name: '', proficiency: 'INTERMEDIATE' });
      invalidate();
    } catch (submitError) {
      setError('skill_name', {
        message: errorMessage(submitError, 'Could not add the skill'),
      });
    }
  }

  if (isPending) return <LoadingState label="Loading your skills…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div>
      <PageHeader
        title="Skills and certificates"
        description="Skills drive matching; a certificate is the part somebody else vouches for."
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card>
          <CardHeader
            title="Your skills"
            description="What companies match you on."
            action={<Badge tone="brand">{data.length}</Badge>}
          />
          {data.length === 0 ? (
            <EmptyState
              icon={<Icon name="skill" size={22} />}
              title="No skills yet"
              description="Add at least three so companies can find you."
            />
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-3">
              {data.map((entry) => (
                <li
                  key={entry.skill.id}
                  className="border-line bg-surface rounded-input hover:border-brand-200 flex items-center gap-3 border p-3 transition"
                >
                  <span
                    aria-hidden="true"
                    className="bg-brand-50 text-brand-600 ring-brand-100 grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset"
                  >
                    <Icon name="skill" size={17} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-ink truncate text-sm font-medium">{entry.skill.name}</p>
                    <Badge tone="brand" className="mt-1">
                      {PROFICIENCY_LABELS[entry.proficiency]}
                    </Badge>
                  </div>
                  <Button
                    variant="ghostDanger"
                    size="xs"
                    className="shrink-0"
                    title={`Remove ${entry.skill.name}`}
                    onClick={() => remove.mutate(entry.skill.id)}
                  >
                    <Icon name="trash" size={14} />
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Add a skill"
            description="Pick from the suggestions to keep names consistent."
          />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label="Skill"
              list="skill-suggestions"
              placeholder="Python, React, Figma…"
              error={errors.skill_name?.message}
              {...register('skill_name', {
                onChange: (event) => setQuery(event.target.value),
              })}
            />
            <datalist id="skill-suggestions">
              {suggestions?.map((skill) => (
                <option key={skill.id} value={skill.name} />
              ))}
            </datalist>

            <Select label="Proficiency" {...register('proficiency')}>
              {Object.entries(PROFICIENCY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>

            <Button type="submit" fullWidth isLoading={isSubmitting}>
              <Icon name="plus" size={16} />
              Add skill
            </Button>
          </form>
        </Card>
      </div>

      <div className="mt-6">
        <CertificationsCard />
      </div>
    </div>
  );
}
